import { useReducer, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";

import { appReducer, initialState } from "@/reducers/app";
import { ConfigSubmissionAction } from "@/reducers/submission";

import { StudyContext, SubmissionContext, SubmissionType } from "@/context/AppContext";

import { onReadFile, onReadFileError } from "@/effects/file";
import { onGetStudy } from "@/effects/study";
import { onUpload } from "@/effects/upload";

import { z } from "zod";

const WeekParamSchema = z.string().regex(/^\d+$/).transform(Number).nullable();

interface AppProviderProps {
  submissionType?: SubmissionType;
  children: React.ReactNode;
}

export function AppProvider({ children, submissionType = "study" }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const id = params.id ?? null;
  const [weekResult] = useState(() => WeekParamSchema.safeParse(searchParams.get("week")));

  useEffect(() => {
    onGetStudy(dispatch, id);
  }, [id, location.key]);

  useEffect(
    () =>
      dispatch({
        type: ConfigSubmissionAction.SetDefaultWeek,
        value: weekResult.data ?? null,
      }),
    [],
  );

  return (
    <StudyContext.Provider value={{ ...state.study }}>
      <SubmissionContext.Provider
        value={{
          ...state.submission,
          type: submissionType,
          setConsentTier: (tier) => dispatch({ type: ConfigSubmissionAction.SetConsentTier, tier }),
          onReadFile: (file) => onReadFile(dispatch, state, submissionType, file),
          onReadFileError: (err) => onReadFileError(dispatch, state, err),
          upload: () => onUpload(dispatch, state, submissionType),
          resetSubmission: () => dispatch({ type: ConfigSubmissionAction.Reset }),
          resetValidation: () => dispatch({ type: ConfigSubmissionAction.ResetValidation }),
          setUserSource: (source) =>
            dispatch({ type: ConfigSubmissionAction.SetUserSource, source }),
          setWeek: (week) => dispatch({ type: ConfigSubmissionAction.SetWeek, value: week }),
          setOverrideDefaultWeek: (override) =>
            dispatch({ type: ConfigSubmissionAction.SetOverrideDefaultWeek, value: override }),
          setOverrideValidation: (override) =>
            dispatch({ type: ConfigSubmissionAction.SetOverrideValidation, value: override }),
        }}
      >
        {children}
      </SubmissionContext.Provider>
    </StudyContext.Provider>
  );
}
